import React, { useState, useEffect } from "react";
import { Box } from "@mui/material";
import {
  CheckCircleOutline as ResolveIcon,
  WarningAmberRounded as WarningIcon,
} from "@mui/icons-material";
import dayjs from "dayjs";
import { dt } from "../../../../shared/themes/redesignTokens";
import { EnrichedServiceReport } from "../../store/serviceReportsSlice";
import serviceReportsApi from "../../api/serviceReportsApi";

// ─── Helpers ────────────────────────────────────────────────

function formatSrNumber(num: string | undefined): string {
  if (!num) return "SR-???";
  if (num.startsWith("SR-")) return num;
  const short = num.length > 4 ? num.slice(-4) : num;
  return `SR-${short}`;
}

// ─── Props ──────────────────────────────────────────────────

interface ResolveSrDialogProps {
  open: boolean;
  item: EnrichedServiceReport;
  onClose: () => void;
  onResolved: (srNumber: string) => void;
  translate: (key: string) => string;
}

// ─── Component ──────────────────────────────────────────────

const ResolveSrDialog: React.FC<ResolveSrDialogProps> = ({
  open,
  item,
  onClose,
  onResolved,
  translate,
}) => {
  const [solvingDescription, setSolvingDescription] = useState("");
  const [costValue, setCostValue] = useState("");
  const [closeDate, setCloseDate] = useState(dayjs().format("YYYY-MM-DD"));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const { sr, linkedWo } = item;
  const hasLinkedWo = Boolean(linkedWo);

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setSolvingDescription("");
      setCostValue("");
      setCloseDate(dayjs().format("YYYY-MM-DD"));
      setError("");
      setSubmitting(false);
    }
  }, [open]);

  // Inject keyframes
  useEffect(() => {
    const id = "resolve-sr-dialog-keyframes";
    if (!document.getElementById(id)) {
      const style = document.createElement("style");
      style.id = id;
      style.textContent = `
        @keyframes resolveSrSlideUp {
          0% { opacity: 0; transform: translateY(24px) scale(0.97); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes resolveSrBackdropIn {
          0% { opacity: 0; }
          100% { opacity: 1; }
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  const canSubmit = solvingDescription.trim().length > 0 && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit || !sr.number) return;
    setSubmitting(true);
    setError("");

    try {
      await serviceReportsApi.resolveSr(sr.number, {
        solvingDescription: solvingDescription.trim(),
        costValue: costValue ? parseFloat(costValue) : undefined,
        closeDate: dayjs(closeDate).toISOString(),
      });
      onResolved(sr.number);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || translate("serviceReports.actions.resolveFailed"));
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <Box
      sx={{
        position: "fixed",
        inset: 0,
        zIndex: 1400,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        animation: "resolveSrBackdropIn 0.2s ease",
      }}
    >
      {/* Backdrop */}
      <Box
        onClick={onClose}
        sx={{
          position: "absolute",
          inset: 0,
          backgroundColor: "rgba(12,17,23,0.25)",
        }}
      />

      {/* Dialog */}
      <Box
        sx={{
          position: "relative",
          width: "100%",
          maxWidth: "440px",
          mx: "16px",
          backgroundColor: "#FFFFFF",
          borderRadius: "20px",
          overflow: "hidden",
          boxShadow: "0 20px 60px rgba(0,0,0,0.12), 0 4px 16px rgba(0,0,0,0.06)",
          animation: "resolveSrSlideUp 0.3s ease",
        }}
      >
        {/* Terracotta accent bar */}
        <Box
          sx={{
            height: "4px",
            background: "linear-gradient(90deg, #D4572B 0%, #E8A817 100%)",
          }}
        />

        {/* Content */}
        <Box sx={{ padding: "28px 28px 24px" }}>
          {/* Header */}
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: "14px", mb: "20px" }}>
            <Box
              sx={{
                width: "36px",
                height: "36px",
                borderRadius: "50%",
                backgroundColor: "rgba(212,87,43,0.10)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <ResolveIcon sx={{ fontSize: "20px", color: "#D4572B" }} />
            </Box>
            <Box>
              <Box
                sx={{
                  fontFamily: dt.font,
                  fontSize: "18px",
                  fontWeight: 700,
                  color: "#0C1117",
                  lineHeight: 1.3,
                }}
              >
                {translate("serviceReports.actions.resolveTitle")}
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: "8px", mt: "4px" }}>
                <Box
                  sx={{
                    fontFamily: dt.fontMono,
                    fontSize: "12px",
                    fontWeight: 600,
                    color: "#D4572B",
                  }}
                >
                  {formatSrNumber(sr.number)}
                </Box>
                <Box
                  sx={{
                    fontFamily: dt.font,
                    fontSize: "13px",
                    color: "#6B7280",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {sr.title}
                </Box>
              </Box>
            </Box>
          </Box>

          {/* WO warning banner */}
          {hasLinkedWo && (
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: "10px",
                backgroundColor: "#FEF9E7",
                border: "1px solid #F9E79F",
                borderRadius: "12px",
                padding: "12px 14px",
                mb: "20px",
              }}
            >
              <WarningIcon sx={{ fontSize: "18px", color: "#E8A817", mt: "1px", flexShrink: 0 }} />
              <Box>
                <Box
                  sx={{
                    fontFamily: dt.font,
                    fontSize: "13px",
                    fontWeight: 600,
                    color: "#0C1117",
                    mb: "2px",
                  }}
                >
                  {translate("serviceReports.actions.linkedWoWarningTitle")}
                </Box>
                <Box
                  sx={{
                    fontFamily: dt.font,
                    fontSize: "12px",
                    color: "#6B7280",
                    lineHeight: 1.5,
                  }}
                >
                  {translate("serviceReports.actions.linkedWoWarningDesc").replace(
                    "{{woNumber}}",
                    linkedWo?.number || "WO-???"
                  )}
                </Box>
              </Box>
            </Box>
          )}

          {/* Solving Description */}
          <Box sx={{ mb: "16px" }}>
            <Box
              sx={{
                fontFamily: dt.font,
                fontSize: "13px",
                fontWeight: 600,
                color: "#0C1117",
                mb: "6px",
              }}
            >
              {translate("serviceReports.actions.solvingDescription")} *
            </Box>
            <textarea
              value={solvingDescription}
              onChange={(e) => setSolvingDescription(e.target.value)}
              placeholder={translate("serviceReports.actions.solvingDescriptionPlaceholder")}
              rows={4}
              style={{
                width: "100%",
                boxSizing: "border-box",
                fontFamily: "'Sora', sans-serif",
                fontSize: "13px",
                lineHeight: 1.6,
                color: "#0C1117",
                backgroundColor: "#F7F5F0",
                border: "1.5px solid transparent",
                borderRadius: "10px",
                padding: "12px 14px",
                resize: "vertical",
                outline: "none",
                transition: "border-color 0.15s ease, box-shadow 0.15s ease",
              }}
              onFocus={(e) => {
                e.target.style.borderColor = "#D4572B";
                e.target.style.boxShadow = "0 0 0 3px rgba(212,87,43,0.10)";
              }}
              onBlur={(e) => {
                e.target.style.borderColor = "transparent";
                e.target.style.boxShadow = "none";
              }}
            />
          </Box>

          {/* Cost + Close Date row */}
          <Box sx={{ display: "flex", gap: "14px", mb: error ? "12px" : "24px" }}>
            {/* Cost */}
            <Box sx={{ flex: 1 }}>
              <Box
                sx={{
                  fontFamily: dt.font,
                  fontSize: "13px",
                  fontWeight: 600,
                  color: "#0C1117",
                  mb: "6px",
                }}
              >
                {translate("serviceReports.actions.cost")}
              </Box>
              <Box sx={{ position: "relative" }}>
                <Box
                  sx={{
                    position: "absolute",
                    left: "14px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    fontFamily: dt.fontMono,
                    fontSize: "13px",
                    fontWeight: 500,
                    color: "#8C9099",
                    pointerEvents: "none",
                  }}
                >
                  EUR
                </Box>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={costValue}
                  onChange={(e) => setCostValue(e.target.value)}
                  placeholder="0.00"
                  style={{
                    width: "100%",
                    boxSizing: "border-box",
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: "13px",
                    color: "#0C1117",
                    backgroundColor: "#F7F5F0",
                    border: "1.5px solid transparent",
                    borderRadius: "10px",
                    padding: "10px 14px 10px 48px",
                    outline: "none",
                    transition: "border-color 0.15s ease, box-shadow 0.15s ease",
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = "#D4572B";
                    e.target.style.boxShadow = "0 0 0 3px rgba(212,87,43,0.10)";
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = "transparent";
                    e.target.style.boxShadow = "none";
                  }}
                />
              </Box>
              <Box
                sx={{
                  fontFamily: dt.font,
                  fontSize: "11px",
                  color: "#D4572B",
                  mt: "4px",
                }}
              >
                {translate("serviceReports.actions.optional")}
              </Box>
            </Box>

            {/* Close Date */}
            <Box sx={{ flex: 1 }}>
              <Box
                sx={{
                  fontFamily: dt.font,
                  fontSize: "13px",
                  fontWeight: 600,
                  color: "#0C1117",
                  mb: "6px",
                }}
              >
                {translate("serviceReports.actions.closeDate")}
              </Box>
              <input
                type="date"
                value={closeDate}
                onChange={(e) => setCloseDate(e.target.value)}
                style={{
                  width: "100%",
                  boxSizing: "border-box",
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: "13px",
                  color: "#D4572B",
                  backgroundColor: "#F7F5F0",
                  border: "1.5px solid transparent",
                  borderRadius: "10px",
                  padding: "10px 14px",
                  outline: "none",
                  transition: "border-color 0.15s ease, box-shadow 0.15s ease",
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = "#D4572B";
                  e.target.style.boxShadow = "0 0 0 3px rgba(212,87,43,0.10)";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "transparent";
                  e.target.style.boxShadow = "none";
                }}
              />
              <Box
                sx={{
                  fontFamily: dt.font,
                  fontSize: "11px",
                  color: "#8C9099",
                  mt: "4px",
                }}
              >
                {translate("serviceReports.actions.defaultsToday")}
              </Box>
            </Box>
          </Box>

          {/* Error message */}
          {error && (
            <Box
              sx={{
                fontFamily: dt.font,
                fontSize: "12px",
                color: "#DC2626",
                backgroundColor: "rgba(220,38,38,0.06)",
                borderRadius: "8px",
                padding: "8px 12px",
                mb: "16px",
              }}
            >
              {error}
            </Box>
          )}

          {/* Buttons */}
          <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: "12px" }}>
            <Box
              onClick={onClose}
              sx={{
                fontFamily: dt.font,
                fontSize: "13px",
                fontWeight: 500,
                color: "#8C9099",
                cursor: "pointer",
                padding: "8px 16px",
                borderRadius: "8px",
                transition: "color 0.15s ease",
                "&:hover": { color: "#4A4F57" },
              }}
            >
              {translate("serviceReports.actions.cancel")}
            </Box>
            <Box
              onClick={handleSubmit}
              sx={{
                fontFamily: dt.font,
                fontSize: "13px",
                fontWeight: 600,
                color: "#FFFFFF",
                background: canSubmit
                  ? "linear-gradient(135deg, #D4572B, #B8451F)"
                  : "#D1CCC4",
                borderRadius: "24px",
                padding: "10px 28px",
                cursor: canSubmit ? "pointer" : "default",
                transition: "all 0.2s ease",
                boxShadow: canSubmit ? "0 2px 8px rgba(212,87,43,0.25)" : "none",
                "&:hover": canSubmit
                  ? {
                      background: "linear-gradient(135deg, #B8451F, #D4572B)",
                      boxShadow: "0 4px 12px rgba(212,87,43,0.30)",
                    }
                  : {},
              }}
            >
              {submitting ? translate("serviceReports.actions.resolving") : translate("serviceReports.actions.resolve")}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ResolveSrDialog;
